/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import { reset, submit } from 'redux-form';
import { useHistory, useLocation } from 'react-router';
import PropTypes from 'prop-types';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { ModalWindow } from '../../semantic-ui/components/modal-window';
import ModalFooterButtons from '../../semantic-ui/components/modal-window/modal-footer-buttons';
import {
	createThermoboxAction,
	getPartnersAction,
	getThermoboxInfoAction,
	updateThermoboxAction,
	resetThermoboxModalAction,
	deleteThermoboxAction,
} from './actions';
import {
	getIsModalShownSelector,
	getIsThermoboxAddFormOpenSelector,
	getIsThermoboxEditFormOpenSelector,
	getIsThermoboxCreatingSelector,
	getIsThermoboxUpdatingSelector,
	getActiveThermoboxIdSelector,
	getIsAuditTabShownSelector,
	getThermoboxInfoSelector,
	getIsThermoboxInfoLoadingSelector,
	getThermoboxNameSelector,
	getIsThermoboxDeletingSelector,
} from './selectors';
import { THERMOBOX_FORM_NAME } from './constants';
import { ThermoboxModalContent } from './components/thermobox-modal-content';
import { Audit } from '../../components/audit';
import { getIsAvailableThermoboxesButtonsSelector } from '../profile/selectors';
import closeModal from '../../utils/closeModal';
import { setWindowInfoAction } from '../alert-modal/actions';
import { removeModalQuery } from '../../utils/removeModalQuery';

const ThermoboxModalWrapper = ({
	isAvailable,
	isModalShown,
	isThermoboxAddFormOpen,
	isThermoboxEditFormOpen,
	isThermoboxCreating,
	isThermoboxUpdating,
	isThermoboxDeleting,
	activeThermoboxId,
	isAuditTabShown,
	isThermoboxInfoLoading,
	thermoboxInfo,
	thermoboxName,
	onFormSubmit,
	onClearForm,
	onResetState,
	onGetPartners,
	onCreateThermobox,
	onUpdateThermobox,
	onDeleteThermobox,
	onGetThermoboxInfo,
	onSetWindowInfo,
}) => {
	if (!isModalShown) return null;
	useInjectSaga({ key: 'thermoboxModalSaga', saga });
	useInjectReducer({ key: 'thermoboxModalReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const modal = query.get('modal');

	const mode = query.get('mode');

	const history = useHistory();

	useEffect(() => {
		if (isThermoboxAddFormOpen || isThermoboxEditFormOpen) {
			onGetPartners();
		}
	}, [isThermoboxAddFormOpen, isThermoboxEditFormOpen]);

	useEffect(() => {
		if (activeThermoboxId) {
			onGetThermoboxInfo(activeThermoboxId);
		}
	}, [activeThermoboxId]);

	const handleModalClose = () => {
		closeModal(query);

		onClearForm();
		onResetState();

		history.push(`${pathname}?${query}`);
	};

	const handleNavItemClick = target => {
		if (target === 'audit') {
			query.set('tab', 'audit');
		} else {
			query.set('modal', target);
			query.delete('tab');
		}

		if (mode) {
			query.delete('mode');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleThermoboxFormOpenButtonClick = mode => {
		if (activeThermoboxId) {
			query.set('id', activeThermoboxId);
		} else {
			query.delete('id');
		}

		if (mode === 'edit') {
			query.set('mode', 'edit');
		}

		if (mode === 'cancel') {
			onClearForm();
			query.delete('mode');
		}

		if (mode === 'cancel' && !activeThermoboxId) {
			onResetState();
			query.delete('modal');
		}

		history.push(`${pathname}?${query}`);
	};

	const handleThermoboxFormSubmit = async () => {
		await onFormSubmit(THERMOBOX_FORM_NAME);

		if (activeThermoboxId) {
			onUpdateThermobox({
				id: activeThermoboxId,
				searchQuery: removeModalQuery(query.toString()),
				redirect: () => handleThermoboxFormOpenButtonClick('cancel'),
			});

			return;
		}

		onCreateThermobox({
			redirect: handleModalClose,
			searchQuery: removeModalQuery(query.toString()),
		});
	};

	const handleKeyDown = e => {
		if (e.key === 'Enter' && e.shiftKey === false) {
			handleThermoboxFormSubmit();
		}
	};

	const handleThermoboxDelete = () => {
		onSetWindowInfo({
			type: 'delete',
			title: 'Вы уверены?',
			text: 'Вы не сможете это отменить',
			button: {
				type: 'success',
				text: 'Да, удалить',
				onClick: () =>
					onDeleteThermobox({
						id: activeThermoboxId,
						redirect: handleModalClose,
						searchQuery: removeModalQuery(query.toString()),
					}),
			},
		});
	};

	return (
		<ModalWindow
			isLoading={isThermoboxInfoLoading}
			isModalShown={isModalShown}
			onKeyDown={handleKeyDown}
			onClose={handleModalClose}
			navItems={
				isThermoboxAddFormOpen || isThermoboxEditFormOpen
					? []
					: [
							{
								id: 'info',
								text: 'Информация',
								onClick: () => handleNavItemClick('thermobox'),
								isActive: modal === 'thermobox' && !isAuditTabShown,
							},
							{
								id: 'audit',
								text: 'Изменения',
								onClick: () => handleNavItemClick('audit'),
								isActive: isAuditTabShown,
							},
					  ]
			}
			headerText={
				isThermoboxAddFormOpen || isThermoboxEditFormOpen
					? `${isThermoboxEditFormOpen ? 'Изменить термоконтейнер' : 'Создать термоконтейнер'}`
					: thermoboxName
			}
			modalContent={
				!isAuditTabShown ? (
					<ThermoboxModalContent
						isLoading={isThermoboxInfoLoading}
						thermoboxInfo={thermoboxInfo}
						isThermoboxFormOpen={isThermoboxAddFormOpen || isThermoboxEditFormOpen}
					/>
				) : (
					<Audit entityName="thermoboxes" entityId={activeThermoboxId} />
				)
			}
			style={{ width: '895px' }}
			actionButtons={[{ type: 'close', onClick: handleModalClose, text: 'profile' }]}
			footerButtons={
				<ModalFooterButtons
					leftButtons={
						!(isThermoboxAddFormOpen || isThermoboxEditFormOpen) && isAvailable.delete && !isAuditTabShown
							? [
									{
										onClick: handleThermoboxDelete,
										text: 'Удалить',
										color: 'warning',
										disabled: isThermoboxInfoLoading || isThermoboxDeleting,
									},
							  ]
							: []
					}
					rightButtons={
						isThermoboxAddFormOpen || isThermoboxEditFormOpen
							? [
									{
										onClick: handleThermoboxFormSubmit,
										text: 'Сохранить',
										color: 'primary',
										loading: isThermoboxCreating || isThermoboxUpdating,
										disabled: isThermoboxInfoLoading || isThermoboxCreating || isThermoboxUpdating,
									},
									{
										onClick: () => handleThermoboxFormOpenButtonClick('cancel'),
										text: 'Отмена',
										color: 'secondary',
										disabled: isThermoboxCreating || isThermoboxUpdating,
									},
							  ]
							: [
									...(isAvailable.update && !isAuditTabShown
										? [
												{
													onClick: () => handleThermoboxFormOpenButtonClick('edit'),
													text: 'Изменить',
													color: 'primary',
													disabled: isThermoboxInfoLoading || isThermoboxDeleting,
												},
										  ]
										: []),
									{
										onClick: handleModalClose,
										text: 'Закрыть',
										color: 'secondary',
									},
							  ]
					}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	isAvailable: getIsAvailableThermoboxesButtonsSelector(),
	isModalShown: getIsModalShownSelector(),
	isThermoboxAddFormOpen: getIsThermoboxAddFormOpenSelector(),
	isThermoboxEditFormOpen: getIsThermoboxEditFormOpenSelector(),
	isThermoboxCreating: getIsThermoboxCreatingSelector(),
	isThermoboxUpdating: getIsThermoboxUpdatingSelector(),
	isThermoboxDeleting: getIsThermoboxDeletingSelector(),
	activeThermoboxId: getActiveThermoboxIdSelector(),
	isAuditTabShown: getIsAuditTabShownSelector(),
	isThermoboxInfoLoading: getIsThermoboxInfoLoadingSelector(),
	thermoboxInfo: getThermoboxInfoSelector(),
	thermoboxName: getThermoboxNameSelector(),
});

const mapDispatchToProps = {
	onFormSubmit: submit,
	onClearForm: () => reset(THERMOBOX_FORM_NAME),
	onResetState: resetThermoboxModalAction,
	onGetPartners: getPartnersAction,
	onCreateThermobox: createThermoboxAction,
	onUpdateThermobox: updateThermoboxAction,
	onDeleteThermobox: deleteThermoboxAction,
	onGetThermoboxInfo: getThermoboxInfoAction,
	onSetWindowInfo: setWindowInfoAction,
};

ThermoboxModalWrapper.propTypes = {
	isAvailable: PropTypes.objectOf(PropTypes.bool),
	isModalShown: PropTypes.bool.isRequired,
	isThermoboxAddFormOpen: PropTypes.bool.isRequired,
	isThermoboxEditFormOpen: PropTypes.bool.isRequired,
	isThermoboxCreating: PropTypes.bool.isRequired,
	isThermoboxUpdating: PropTypes.bool.isRequired,
	isThermoboxDeleting: PropTypes.bool.isRequired,
	activeThermoboxId: PropTypes.string,
	isAuditTabShown: PropTypes.bool.isRequired,
	isThermoboxInfoLoading: PropTypes.bool.isRequired,
	thermoboxInfo: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			title: PropTypes.string.isRequired,
			value: PropTypes.oneOfType([
				PropTypes.string,
				PropTypes.shape({
					name: PropTypes.string,
					role: PropTypes.string,
				}),
				PropTypes.shape({
					length: PropTypes.string,
					width: PropTypes.string,
					height: PropTypes.string,
				}),
			]).isRequired,
		}),
	),
	thermoboxName: PropTypes.string.isRequired,
	onFormSubmit: PropTypes.func.isRequired,
	onClearForm: PropTypes.func.isRequired,
	onResetState: PropTypes.func.isRequired,
	onGetPartners: PropTypes.func.isRequired,
	onCreateThermobox: PropTypes.func.isRequired,
	onUpdateThermobox: PropTypes.func.isRequired,
	onDeleteThermobox: PropTypes.func.isRequired,
	onGetThermoboxInfo: PropTypes.func.isRequired,
	onSetWindowInfo: PropTypes.func.isRequired,
};

const ThermoboxModal = connect(mapStateToProps, mapDispatchToProps)(ThermoboxModalWrapper);

export default ThermoboxModal;
